import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { cn } from "../../lib/utils";
import {
    LayoutGrid,
    Contact,
    Activity,
    UsersRound,
    Briefcase,
    Menu,
    Settings,
    ChevronRight,
    ChevronLeft,
    X,
    LogOut,
    Eye,
    ListChecks,
    UserCog,
    BookOpen,
    Sparkles,
    Calendar,
    Gamepad2,
    ShieldCheck,
    Bot,
    GitMerge,
    Wand2,
    Mail,
    Timer,
    Clock,
    Inbox
} from "lucide-react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { useAuth } from "../../hooks/useAuth";
import { unreadCount } from "../../services/crmServices/mailService";
import NotificationDropdown from "../notifications/NotificationDropdown";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuLabel
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { getFloorLabel, getFullName } from "../../services/common/util";
import { useNavigate } from "react-router-dom";

// Navigation items configuration
const navigationItems = [
    { name: "Dashboard", href: "/", icon: LayoutGrid },
    { name: "Leads", href: "/leads", icon: Contact },
    { name: "Affiliator Leads", href: "/live-leads", icon: Activity, adminOnly: true },
    { name: "Users", href: "/staff", icon: UsersRound },
    { name: "Desks / Floors", href: "/desks", icon: Briefcase },
    { name: "Calendar", href: "/calendar", icon: Calendar },
    { name: "Inbox", href: "/inbox", icon: Inbox },
    { name: "My Work", href: "/my-work", icon: Timer, staffOnly: true },
    { name: "AI Assistant", href: "/ai-assistant", icon: Sparkles },
    { name: "AI Caller", href: "/ai-caller", icon: Bot, allowedRoles: ["admin", "floorManager"] },
    { name: "AI Agents", href: "/ai-agents", icon: Wand2, adminOnly: true },
    { name: "Games", href: "/games", icon: Gamepad2, gamesGate: true },
    { type: "separator", adminOnly: true },
    { name: "Settings", href: "/settings/system", icon: Settings, adminOnly: true },
    { name: "Lead Management", href: "/settings/lead-status", icon: ListChecks, adminOnly: true },
    { name: "Positions", href: "/settings/positions", icon: UserCog, adminOnly: true },
    { name: "Work Tracking", href: "/settings/work-tracking", icon: Clock, adminOnly: true },
    { name: "Export Audit", href: "/settings/export-audit", icon: ShieldCheck, adminOnly: true },
    { name: "Incoming Duplicates", href: "/settings/incoming-duplicates", icon: GitMerge, adminOnly: true },
    { name: "Email Templates", href: "/settings/email-templates", icon: Mail, adminOnly: true },
    { type: "separator", adminOnly: true },
    { name: "Documentation", href: "/documentation", icon: BookOpen, adminOnly: true }
];

export function Navigation() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(() => {
        return localStorage.getItem("sidebarCollapsed") === "true";
    });
    const [mailUnread, setMailUnread] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const load = () => {
            unreadCount()
                .then((res) => {
                    if (!cancelled) setMailUnread(res.data?.unread || 0);
                })
                .catch(() => {});
        };
        load();
        const t = setInterval(load, 30000);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, []);

    useEffect(() => {
        document.documentElement.style.setProperty("--sidebar-width", isCollapsed ? "64px" : "256px");
    }, [isCollapsed]);

    const toggleCollapse = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        localStorage.setItem("sidebarCollapsed", newState.toString());
        document.documentElement.style.setProperty("--sidebar-width", newState ? "64px" : "256px");
        window.dispatchEvent(new Event("sidebarToggle"));
    };

    // Filter navigation items based on user role
    const filteredItems = navigationItems.filter((item) => {
        if (!user?.position?.role) return false;
        if (item.adminOnly && user.position.role !== "admin") return false;
        if (item.staffOnly && user.position.role === "admin") return false;
        if (item.allowedRoles && !item.allowedRoles.includes(user.position.role)) return false;
        if (user.position.role === "agent" && (item.name === "Users" || item.name === "Desks / Floors")) return false;
        if (item.gamesGate && !user?.permissions?.games?.access) return false;
        return true;
    });

    const getInitials = (name) => {
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2);
    };

    const getPositionColor = (position) => {
        if (!position || !position.role) {
            return "bg-gray-100 text-gray-800 border-gray-200";
        }
        switch (position.role) {
            case "admin":
                return "bg-red-50 text-red-700 border-red-200";
            case "floorManager":
                return "bg-amber-50 text-amber-700 border-amber-200";
            case "deskManager":
                return "bg-sky-50 text-sky-700 border-sky-200";
            default:
                return "bg-emerald-50 text-emerald-700 border-emerald-200";
        }
    };

    const handleViewProfile = () => {
        navigate("/profile");
    };

    // Navigation content component
    const NavigationContent = ({ onItemClick, collapsed = false }) => (
        <nav className="flex flex-col h-full">
            {/* Logo/Brand */}
            <div className={cn(
                "border-b border-gray-200 flex items-center flex-shrink-0",
                collapsed ? "px-2 py-4 justify-center" : "px-5 py-4"
            )}>
                {/* Hexagon logo */}
                <svg className="flex-shrink-0" width={collapsed ? "28" : "32"} height={collapsed ? "28" : "32"} viewBox="0 0 40 40" fill="none">
                    <polygon points="20,2 37,11 37,29 20,38 3,29 3,11" fill="#DB0011" />
                    <polygon points="20,8 31,14 31,26 20,32 9,26 9,14" fill="white" />
                    <polygon points="20,13 26,16.5 26,23.5 20,27 14,23.5 14,16.5" fill="#DB0011" />
                </svg>
                {!collapsed && (
                    <div className="ml-3">
                        <h1 className="text-base font-bold text-gray-900 leading-tight">SimpleCRM</h1>
                        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Management Portal</p>
                    </div>
                )}
            </div>

            {/* Navigation Items */}
            <div className="flex-1 min-h-0 overflow-y-auto py-3">
                <div className={cn("space-y-0.5", collapsed ? "px-2" : "px-3")}>
                    {filteredItems.map((item, index) => {
                        if (item.type === "separator") {
                            return (
                                <div key={`sep-${index}`} className={cn("py-2", collapsed ? "px-1" : "px-2")}>
                                    <div className="border-t border-gray-200" />
                                </div>
                            );
                        }

                        const isActive = location.pathname === item.href;
                        const Icon = item.icon;

                        if (collapsed) {
                            return (
                                <Link
                                    key={item.name}
                                    to={item.href}
                                    onClick={onItemClick}
                                    className={cn(
                                        "w-full flex items-center justify-center p-2.5 rounded-lg relative transition-colors",
                                        isActive
                                            ? "bg-red-50 text-red-700"
                                            : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                                    )}
                                    title={item.name}
                                >
                                    <Icon className="h-[18px] w-[18px]" />
                                    {item.name === "Inbox" && mailUnread > 0 && (
                                        <span className="absolute top-1 right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-600 text-white text-[9px] leading-[14px] text-center">
                                            {mailUnread > 9 ? "9+" : mailUnread}
                                        </span>
                                    )}
                                </Link>
                            );
                        }

                        return (
                            <Link
                                key={item.name}
                                to={item.href}
                                onClick={onItemClick}
                                className={cn(
                                    "w-full flex items-center px-3 py-2.5 text-[13px] font-medium rounded-lg transition-colors",
                                    isActive
                                        ? "bg-red-50 text-red-700 border-l-[3px] border-red-600 -ml-[3px] pl-[15px]"
                                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                                )}
                            >
                                <Icon className="h-[18px] w-[18px] mr-3" />
                                <span>{item.name}</span>
                                {item.name === "Inbox" && mailUnread > 0 && (
                                    <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] leading-[18px] text-center">
                                        {mailUnread > 99 ? "99+" : mailUnread}
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </div>
            </div>

            {/* Bottom Section - Notifications and User Profile */}
            <div className={cn("border-t border-gray-200 flex-shrink-0", collapsed ? "p-2 space-y-2" : "p-3 space-y-2")}>
                {/* Notifications */}
                <div className={cn("flex items-center", collapsed ? "flex-col space-y-2" : "gap-2")}>
                    {collapsed ? (
                        <div className="flex items-center justify-center">
                            <NotificationDropdown />
                        </div>
                    ) : (
                        <div className="flex-1">
                            <NotificationDropdown />
                        </div>
                    )}
                </div>

                {/* User Profile */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className={cn(
                                "w-full flex items-center rounded-lg hover:bg-gray-100 transition-colors",
                                collapsed ? "justify-center p-2" : "px-3 py-2"
                            )}
                        >
                            <Avatar className={cn("flex-shrink-0", collapsed ? "h-8 w-8" : "h-9 w-9")}>
                                <AvatarFallback className="bg-red-600 text-white text-sm font-semibold">
                                    {user?.firstName ? getInitials(getFullName(user)) : "U"}
                                </AvatarFallback>
                            </Avatar>
                            {!collapsed && (
                                <div className="flex-1 ml-3 text-left min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                        {getFullName(user)}
                                    </div>
                                    <div className="text-xs text-gray-400 truncate">
                                        {user?.position?.name || "No Position"}
                                    </div>
                                </div>
                            )}
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align={collapsed ? "start" : "end"}
                        side={collapsed ? "right" : "top"}
                        className="w-80 bg-white border border-gray-200 p-2"
                    >
                        <DropdownMenuLabel className="px-3 py-3">
                            <div className="flex items-center space-x-3">
                                <Avatar className="h-12 w-12">
                                    <AvatarFallback className="bg-red-600 text-white font-semibold">
                                        {user?.firstName ? getInitials(getFullName(user)) : "U"}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 truncate">
                                        {getFullName(user)}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                                    <div className="flex items-center space-x-2 mt-1">
                                        <Badge
                                            variant="outline"
                                            className={`text-xs ${getPositionColor(user?.position)}`}
                                        >
                                            {user?.position?.name || "No Position"}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </DropdownMenuLabel>
                        {user?.position?.role !== "admin" && (
                            <div className="px-3 py-2 bg-gray-50 rounded-md mx-2 my-2">
                                {/deskManager|floorManager/.test(user?.position?.role) && (
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                            <span className="text-gray-500">Floor:</span>
                                            <span className="ml-1 font-medium text-gray-700">
                                                {user?.position?.floor
                                                    ? getFloorLabel(user?.position?.floor)
                                                    : "None"}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Desks:</span>
                                            <span className="ml-1 font-medium text-gray-700">
                                                {user?.desksCount || 0}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {user?.position?.role === "agent" && (
                                    <div className="grid grid-cols-3 gap-2 text-xs">
                                        <div className="col-span-3">
                                            <span className="text-gray-500">Floor:</span>
                                            <span className="ml-1 font-medium text-gray-700">
                                                {user?.position?.floor
                                                    ? getFloorLabel(user?.position?.floor)
                                                    : "None"}
                                            </span>
                                        </div>
                                        <div className="col-span-2">
                                            <span className="text-gray-500">Desk:</span>
                                            <span className="ml-1 font-medium text-gray-700">
                                                {user?.agentDesk?.name || "None"}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Leads:</span>
                                            <span className="ml-1 font-medium text-gray-700">
                                                {user?.leadsCount || 0}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        <DropdownMenuSeparator className="my-2" />
                        <DropdownMenuItem
                            className="cursor-pointer hover:bg-gray-100 rounded-md px-3 py-2"
                            onClick={handleViewProfile}
                        >
                            <Eye className="h-4 w-4 mr-3 text-gray-500" />
                            <div className="flex flex-col">
                                <span className="text-sm font-medium">View Profile</span>
                                <span className="text-xs text-gray-500">See your profile information</span>
                            </div>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="my-2" />
                        <DropdownMenuItem
                            className="cursor-pointer hover:bg-red-50 rounded-md px-3 py-2 text-red-600"
                            onClick={logout}
                        >
                            <LogOut className="h-4 w-4 mr-3" />
                            <div className="flex flex-col">
                                <span className="text-sm font-medium">Sign Out</span>
                                <span className="text-xs text-red-500">Log out of your account</span>
                            </div>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Collapse Toggle Button */}
            {!collapsed && (
                <div className="border-t border-gray-200 p-2 flex-shrink-0">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={toggleCollapse}
                        className="w-full justify-start text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                    >
                        <ChevronLeft className="h-4 w-4 mr-2" />
                        <span className="text-xs">Collapse</span>
                    </Button>
                </div>
            )}
        </nav>
    );

    return (
        <>
            {/* Desktop Sidebar */}
            <aside className={cn(
                "hidden md:flex md:flex-col md:fixed md:inset-y-0 z-30",
                isCollapsed ? "md:w-16" : "md:w-64"
            )}>
                <div className="flex-1 flex flex-col bg-white border-r border-gray-200">
                    <NavigationContent collapsed={isCollapsed} />
                    {isCollapsed && (
                        <div className="border-t border-gray-200 p-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={toggleCollapse}
                                className="w-full justify-center p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                                title="Expand"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    )}
                </div>
            </aside>

            {/* Mobile Navigation - Drawer */}
            <div className="md:hidden">
                <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                    <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="fixed top-4 left-4 z-50 bg-white text-gray-700 hover:bg-gray-100 shadow-md border border-gray-200">
                            <Menu className="h-5 w-5" />
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-64 p-0 bg-white border-gray-200">
                        <div className="flex flex-col h-full bg-white">
                            <div className="flex items-center justify-between p-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                                <SheetTrigger asChild>
                                    <Button variant="ghost" size="icon" className="text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                                        <X className="h-5 w-5" />
                                    </Button>
                                </SheetTrigger>
                            </div>
                            <NavigationContent onItemClick={() => setMobileMenuOpen(false)} collapsed={false} />
                        </div>
                    </SheetContent>
                </Sheet>
            </div>
        </>
    );
}
